import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, Subscription, take, tap } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MessageService } from 'primeng/api';

import { ControlValueAccessorModel, getProvider } from '../../../Forms/models/control-value-accessor.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';

import { BrandIdentityService } from '../../services/brand-identity.service';
import { BrandIdentity } from '../../models/brand-identity';
import { BrandCompetitor } from '../../../../api/brandIdentity/models/brand-identity.api.model';

interface BrandCompetitorOptionValue {
    id?: number;
    brand?: BrandIdentity;
    errors?: {
        competitorExists?: boolean;
    };
}
@Component({
    selector: 'agpr-brand-competitors',
    templateUrl: './brand-competitors.component.html',
    styleUrls: ['./brand-competitors.component.scss'],
    providers: [getProvider(BrandCompetitorsComponent)],
    standalone: false
})
export class BrandCompetitorsComponent extends ControlValueAccessorModel<BrandCompetitor[]> implements OnInit {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;

    @Input() public id: string = 'brand-competitors';
    @Input() public brand: BrandIdentity;
    @Input() public label: string = 'Common.brandInformation.competitors.label';
    // Pass null or '' to hide the icon.
    @Input() public infoTooltip: string | null = 'Common.brandInformation.competitors.tooltip';
    @Input() public hideItemActions: boolean = false;
    @Input() public disabled: boolean = false;
    @Input() public maxLimit: number = 10;
    @Input() public tooltipPosition: string = 'top';

    private subscriptions: Subscription[] = [];
    protected suggestions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject<DropdownOptionModel[]>([]);

    overlayContainer: Element;
    _value: DropdownOptionModel[] = [];
    brandList: BrandIdentity[] = [];
    options: DropdownOptionModel[] = [];
    hasEmptyCompetitors: boolean = false;

    @Output() createCompetitor = new EventEmitter<string>();
    @Output() selectCompetitor = new EventEmitter<number>();

    constructor(
        private localizationService: LocalizationService,
        private messageService: MessageService,
        private brandIdentityService: BrandIdentityService
    ) {
        super();
        this.overlayContainer = document.getElementById('main-content');
    }

    ngOnInit() {
        this.subscriptions.push(
            this.brandIdentityService.all$.subscribe(list => {
                this.brandList = list ?? [];
                this.updateSuggestions();
                this.populateCompetitors();
            })
        );
    }

    drop(event: CdkDragDrop<any[]>) {
        if (event.previousContainer !== event.container) {
            const brand = event.previousContainer.data.find(x => x.id === +event.item.element.nativeElement.dataset.id);
            if(brand && brand.id !== this.brand?.id) {
                this._value.push(new DropdownOptionModel(brand.name, {id: brand.id, brand} as BrandCompetitorOptionValue));
                this.emitValue();
            }
        } else {
            moveItemInArray(this._value, event.previousIndex, event.currentIndex);
        }
    }

    addCompetitor() {
        this._value.push(new DropdownOptionModel('', {id: null, brand: null} as BrandCompetitorOptionValue));
        this.hasEmptyCompetitors = true;
        this.emitValue();
    }

    private validateCompetitors(): boolean {
        return this._value.reduce((isValid, competitor) => {
            const isCompetitorExists = this._value.some(x => x !== competitor
                && competitor.label
                && x.label.toLocaleLowerCase() === competitor.label.toLocaleLowerCase());
            competitor.value.errors = { competitorExists: isCompetitorExists};
            return isValid && !isCompetitorExists;
        }, true);
    }

    private updateSuggestions() {
        this.options = this.brandList
            .filter(x => x && x.id !== this.brand?.id && !this._value?.some(v => v.value?.id === x.id))
            .map(x => new DropdownOptionModel(x.name, {id: x.id, brand: x} as BrandCompetitorOptionValue));
        this.suggestions$.next(this.options);
    }

    private populateCompetitors() {
        this._value = this._value.map(item => {
            const brand = this.brandList.find(itm => itm.id === item.value.id);
            if (brand) {
                item.label = brand.name;
                item.value.brand = brand;
            }
            return item;
        });
    }

    private isNameDuplicated(name: string): Observable<boolean> {
        return this.brandIdentityService.isNameDuplicated$(name).pipe(
            tap(isDuplicated => {
                if (isDuplicated) {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Common.brandIdentity.duplicateName.heading',
                        detail: this.localizationService.transform('Common.brandIdentity.duplicateName.message', { name })
                    });
                }
            }),
            take(1)
        );
    }

    onCompetitorChange(event: DropdownOptionModel | string, competitor: DropdownOptionModel) {
        if (!event) {
            competitor.label = '';
            competitor.value = {id: null};
        } else if (typeof event === 'string') {
            competitor.label = event.trim();
            competitor.value = {id: null};
        } else {
            competitor.label = event.label;
            competitor.value = event.value;
        }
        this.hasEmptyCompetitors = this._value.some(x => !x.label.trim());
        const isValid = this.validateCompetitors();
        if (isValid) {
            this.emitValue();
        }
    }

    async createBrand(option: DropdownOptionModel) {
        const name = option.label.trim();
        if (!name) {
            return;
        }

        const isNameDuplicated = await firstValueFrom(this.isNameDuplicated(name));
        if (isNameDuplicated) {
            return;
        }

        this.createCompetitor.emit(name);
    }

    editCompetitor(option: DropdownOptionModel) {
        this.selectCompetitor.emit(option.value.id);
    }

    removeCompetitor(index: number) {
        this._value.splice(index, 1);
        this.validateCompetitors();
        if (this._value.length === 0) {
            this.addCompetitor();
        } else {
            this.hasEmptyCompetitors = this._value.some(x => !x.label.trim());
            this.emitValue();
        }
    }

    emitValue() {
        const competitors = this._value.filter(x => !!x.label).map(x => ({id: x.value?.id, name: x.label } as BrandCompetitor));
        this.value = competitors;
        this.onModelChange(this.value);
        this.onModelTouched();
    }

    override writeValue(value: BrandCompetitor[]): void {
        this.value = value?.length ? value : [{id: null, name: ''}];
        this._value = this.value.map(x => new DropdownOptionModel(x.name, {id: x.id} as BrandCompetitorOptionValue));
        this.hasEmptyCompetitors = this._value.some(x => !x.label.trim());
        this.populateCompetitors();
        this.updateSuggestions();
    }

    override setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
